/**
 * Resolves a song to the music video that Mirror Drift should show.
 *
 * The resolution order, most specific first:
 *
 *   1. a video **inside the Song Pack**, handed in as an object URL
 *   2. `manifest.extensions.mvFile` — a reference to a file outside the pack
 *   3. `<displayName>.mp4` in the video directory
 *   4. no video, and Mirror Drift disables itself
 *
 * One and two are the same manifest key. The runtime resolves it against the
 * archive first: if `mvFile` names an entry the pack actually carries, the video
 * travels with the song and arrives here as `packVideoUrl`. If it names anything
 * else, it is an external reference and is tried as one. That is what lets a
 * pack whose video entry went missing — Unity's rebuild export preserves
 * `extensions` but drops unknown entries — fall back instead of breaking.
 *
 * Rule three is the compatibility path and stays exactly as it was: every Song
 * Pack made before videos could be bundled resolves its MV by name, and still
 * does.
 *
 * The view layer must not import from `songpack/`, so this module takes a plain
 * `MirrorVideoRequest` rather than a `ManifestV1`. Wiring the real manifest in
 * later is a matter of mapping its `extensions` array onto `extensions` here —
 * no logic in this file has to change. That mapping belongs to whoever owns both
 * layers (the runtime), which is exactly where the boundary should sit.
 *
 * Version 1 already reserves `extensions` as free-form string key/value pairs, so
 * carrying an MV reference needs no Song Pack format change at all.
 */

/** Directory the videos are served from, relative to the site root. */
export const VIDEO_DIRECTORY = '/video';

/** Extension key that names an explicit MV file for a song. */
export const MV_FILE_EXTENSION_KEY = 'mvFile';

export interface MirrorVideoRequest {
  /** The song's canonical name, shown verbatim in UI and used as the file name. */
  readonly displayName: string;
  /**
   * Object URL for a video bundled inside the Song Pack.
   *
   * A URL rather than a `Blob`, so the view layer never touches the library or
   * the format. Whoever creates it owns revoking it — see
   * `MirrorDriftView.unload`.
   */
  readonly packVideoUrl?: string | undefined;
  /**
   * Manifest extension properties, if a Song Pack is loaded. Accepts either the
   * array shape version 1 serialises or a plain record, so the eventual caller
   * can pass whichever it holds.
   */
  readonly extensions?: ReadonlyArray<{ key: string; value: string }> | Readonly<Record<string, string>>;
}

export interface MirrorVideoCandidate {
  readonly url: string;
  /** Which rule produced this candidate. Reported in the load diagnostics. */
  readonly origin: 'song-pack' | 'manifest-extension' | 'display-name';
}

/**
 * Builds the ordered candidate list. Returns an empty array when nothing can be
 * derived, which the caller treats as "no MV" rather than as an error.
 */
export function resolveMirrorVideoCandidates(
  request: MirrorVideoRequest,
): MirrorVideoCandidate[] {
  const candidates: MirrorVideoCandidate[] = [];

  // First, unconditionally: a video the song carries with it is the song's own
  // video, and nothing outside the pack should be able to displace it.
  if (request.packVideoUrl !== undefined && request.packVideoUrl.length > 0) {
    candidates.push({ url: request.packVideoUrl, origin: 'song-pack' });
  }

  // Skipped when the pack supplied one: the reference names that very entry, so
  // trying it as an external path would look for `/video/video.mp4`, which is
  // either nothing or — worse — somebody else's file.
  const override =
    request.packVideoUrl === undefined
      ? readExtension(request.extensions, MV_FILE_EXTENSION_KEY)
      : undefined;
  if (override !== undefined && override.trim().length > 0) {
    candidates.push({ url: toVideoUrl(override.trim()), origin: 'manifest-extension' });
  }

  const name = request.displayName.trim();
  if (name.length > 0) {
    const url = toVideoUrl(`${name}.mp4`);
    // An override that already resolves to the same file should not be probed twice.
    if (!candidates.some((candidate) => candidate.url === url)) {
      candidates.push({ url, origin: 'display-name' });
    }
  }

  return candidates;
}

/**
 * Turns a reference into a fetchable URL.
 *
 * A reference that is already absolute (an http(s) URL or a rooted path) is taken
 * as authored. Anything else is treated as a bare file name inside the video
 * directory and percent-encoded as a **single path segment** — song titles here
 * contain spaces, full-width punctuation and `♡`, none of which survive being
 * dropped into a URL raw.
 */
export function toVideoUrl(reference: string): string {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(reference)) return reference;
  if (reference.startsWith('/')) return encodePath(reference);
  return `${VIDEO_DIRECTORY}/${encodeURIComponent(reference)}`;
}

/** Encodes each segment of an already-rooted path, leaving the separators alone. */
function encodePath(path: string): string {
  return path
    .split('/')
    .map((segment) => (segment.length === 0 ? segment : encodeURIComponent(segment)))
    .join('/');
}

function readExtension(
  extensions: MirrorVideoRequest['extensions'],
  key: string,
): string | undefined {
  if (extensions === undefined) return undefined;
  if (Array.isArray(extensions)) {
    return extensions.find((property) => property.key === key)?.value;
  }
  return (extensions as Readonly<Record<string, string>>)[key];
}
